import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { createEvent, createEventKeepalive } from "../../services/post";
import { getCachedAccessToken } from "../AuthContext/RequestInterceptor";
import { useClientAccessContext } from "../ClientAccessContext";
import { useConfig } from "../ConfigContext";

const SESSION_ID_STORAGE_KEY = "eventTrackingSessionId";
const SESSION_STARTED_STORAGE_KEY = "eventTrackingStartedSessionId";
const SESSION_LAST_SEEN_STORAGE_KEY = "eventTrackingLastSeenAt";
const SESSION_CLIENT_IDS_STORAGE_KEY = "eventTrackingSelectedClientIds";
const SESSION_TOPIC_IDS_STORAGE_KEY = "eventTrackingSelectedTopicIds";
const SESSION_INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

export enum EventName {
	SESSION_STARTED = "session_started",
	CLIENT_SELECTED = "client_selected",
	TOPIC_SELECTED = "topic_selected",
	ARTICLE_OPENED = "article_opened",
	ARTICLE_RELEVANCE_VOTED = "article_relevance_voted",
	FILTERS_APPLIED = "filters_applied",
	NEWSLETTER_SENT = "newsletter_sent",
	SESSION_LAST_SEEN = "session_last_seen",
	SESSION_ENDED = "session_ended",
}

interface TrackEventOptions {
	documentId?: number | null;
	properties?: Record<string, unknown>;
}

interface IEventTrackingProvider {
	sessionId: string;
	trackEvent: (name: EventName, options?: TrackEventOptions) => Promise<void>;
	endSession: () => Promise<void>;
}

interface EventTrackingProps {
	children?: React.ReactNode;
}

const EventTrackingContext = createContext<IEventTrackingProvider | undefined>(
	undefined
);

const getSessionId = () => {
	const existingSessionId = window.sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
	if (existingSessionId) return existingSessionId;

	const sessionId = crypto.randomUUID();
	window.sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
	window.sessionStorage.setItem(
		SESSION_LAST_SEEN_STORAGE_KEY,
		Date.now().toString()
	);
	return sessionId;
};

const updateLastSeen = () => {
	window.sessionStorage.setItem(
		SESSION_LAST_SEEN_STORAGE_KEY,
		Date.now().toString()
	);
};

const getTrackedIds = (key: string): number[] =>
	JSON.parse(window.sessionStorage.getItem(key) ?? "[]");

const addTrackedId = (key: string, id: number) =>
	window.sessionStorage.setItem(
		key,
		JSON.stringify([...getTrackedIds(key), id])
	);

const clearTrackedIds = () => {
	window.sessionStorage.removeItem(SESSION_CLIENT_IDS_STORAGE_KEY);
	window.sessionStorage.removeItem(SESSION_TOPIC_IDS_STORAGE_KEY);
};

const EventTrackingProvider: React.FunctionComponent<EventTrackingProps> = ({
	children,
}) => {
	const { selectedClient, userWithClients } = useClientAccessContext();
	const { selectedTopic } = useConfig();
	const [sessionId, setSessionId] = useState(getSessionId);
	const sessionStartInFlightRef = useRef<string | null>(null);
	const sessionEndedRef = useRef(false);

	const sendEvent = useCallback(
		async (name: EventName, options?: TrackEventOptions) => {
			updateLastSeen();

			try {
				const response = await createEvent({
					sessionId,
					documentId: options?.documentId ?? null,
					name,
					properties: options?.properties
						? JSON.stringify(options.properties)
						: null,
				});
				return response !== undefined;
			} catch (error) {
				console.error("Error while tracking event", name, error);
				return false;
			}
		},
		[sessionId]
	);

	const trackEvent = useCallback(
		async (name: EventName, options?: TrackEventOptions) => {
			await sendEvent(name, options);
		},
		[sendEvent]
	);

	const startNewSession = useCallback(() => {
		const newSessionId = crypto.randomUUID();
		window.sessionStorage.setItem(SESSION_ID_STORAGE_KEY, newSessionId);
		window.sessionStorage.removeItem(SESSION_STARTED_STORAGE_KEY);
		clearTrackedIds();
		updateLastSeen();

		sessionStartInFlightRef.current = null;
		sessionEndedRef.current = false;
		setSessionId(newSessionId);
	}, []);

	const endSession = useCallback(async () => {
		if (sessionEndedRef.current) return;
		sessionEndedRef.current = true;

		const properties = {
			clientId: selectedClient?.clientId,
			clientName: selectedClient?.clientName,
			topicId: selectedTopic?.id,
			topicName: selectedTopic?.name,
		};
		const accessToken = getCachedAccessToken();
		const endRequest =
			accessToken && selectedClient?.clientId
				? createEventKeepalive(
						{
							sessionId,
							name: EventName.SESSION_ENDED,
							properties: JSON.stringify(properties),
						},
						accessToken,
						selectedClient.clientId
				  ).catch((error) =>
						console.error("Error while tracking session_ended", error)
				  )
				: trackEvent(EventName.SESSION_ENDED, { properties });

		window.sessionStorage.removeItem(SESSION_ID_STORAGE_KEY);
		window.sessionStorage.removeItem(SESSION_STARTED_STORAGE_KEY);
		window.sessionStorage.removeItem(SESSION_LAST_SEEN_STORAGE_KEY);
		clearTrackedIds();
		await endRequest;
	}, [selectedClient, selectedTopic, sessionId, trackEvent]);

	useEffect(() => {
		if (!selectedClient?.clientId || !userWithClients?.id) return;
		if (
			window.sessionStorage.getItem(SESSION_STARTED_STORAGE_KEY) === sessionId
		)
			return;
		if (sessionStartInFlightRef.current === sessionId) return;

		sessionStartInFlightRef.current = sessionId;
		void sendEvent(EventName.SESSION_STARTED, {
			properties: {
				clientId: selectedClient.clientId,
				clientName: selectedClient.clientName,
			},
		}).then((sent) => {
			if (sent) {
				window.sessionStorage.setItem(SESSION_STARTED_STORAGE_KEY, sessionId);
			}
			sessionStartInFlightRef.current = null;
		});
	}, [selectedClient, sendEvent, sessionId, userWithClients?.id]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				if (sessionEndedRef.current || !selectedClient?.clientId) return;

				updateLastSeen();
				const properties = {
					clientId: selectedClient.clientId,
					clientName: selectedClient.clientName,
					topicId: selectedTopic?.id,
					topicName: selectedTopic?.name,
				};
				const accessToken = getCachedAccessToken();
				if (!accessToken) {
					void trackEvent(EventName.SESSION_LAST_SEEN, { properties });
					return;
				}

				void createEventKeepalive(
					{
						sessionId,
						name: EventName.SESSION_LAST_SEEN,
						properties: JSON.stringify(properties),
					},
					accessToken,
					selectedClient.clientId
				).catch((error) =>
					console.error("Error while tracking session_last_seen", error)
				);
				return;
			}

			const lastSeenAt = Number(
				window.sessionStorage.getItem(SESSION_LAST_SEEN_STORAGE_KEY)
			);
			if (
				lastSeenAt &&
				Date.now() - lastSeenAt >= SESSION_INACTIVITY_TIMEOUT_MS
			) {
				startNewSession();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () =>
			document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, [selectedClient, selectedTopic, sessionId, startNewSession, trackEvent]);

	useEffect(() => {
		if (!selectedClient?.clientId || !userWithClients?.id) return;
		if (
			getTrackedIds(SESSION_CLIENT_IDS_STORAGE_KEY).includes(
				selectedClient.clientId
			)
		)
			return;

		addTrackedId(SESSION_CLIENT_IDS_STORAGE_KEY, selectedClient.clientId);
		trackEvent(EventName.CLIENT_SELECTED, {
			properties: {
				clientId: selectedClient.clientId,
				clientName: selectedClient.clientName,
			},
		});
	}, [selectedClient, trackEvent, userWithClients?.id]);

	useEffect(() => {
		if (!selectedTopic?.id || !selectedClient?.clientId || !userWithClients?.id)
			return;
		if (getTrackedIds(SESSION_TOPIC_IDS_STORAGE_KEY).includes(selectedTopic.id))
			return;

		addTrackedId(SESSION_TOPIC_IDS_STORAGE_KEY, selectedTopic.id);
		trackEvent(EventName.TOPIC_SELECTED, {
			properties: {
				clientId: selectedClient.clientId,
				clientName: selectedClient.clientName,
				topicId: selectedTopic.id,
				topicName: selectedTopic.name,
			},
		});
	}, [selectedClient, selectedTopic, trackEvent, userWithClients?.id]);

	const eventTrackingContextValue = {
		sessionId,
		trackEvent,
		endSession,
	};

	return (
		<EventTrackingContext.Provider value={eventTrackingContextValue}>
			{children}
		</EventTrackingContext.Provider>
	);
};

const useEventTracking = () => {
	const context = React.useContext(EventTrackingContext);
	if (context === undefined) {
		throw new Error(
			"useEventTracking must be used within an EventTrackingProvider"
		);
	}
	return context;
};

export { EventTrackingProvider, useEventTracking };
